/**
 * Payee memory. When the owner classifies a payment to a person or merchant,
 * the app can remember the payee so the same payee is classified automatically
 * next month. Matching is plain text containment on a normalised description,
 * the same normalisation the database uses, so behaviour is predictable.
 */

/** Uppercase, non-alphanumerics to single spaces. Mirrors the SQL in resolve_review. */
export function normaliseText(s: string): string {
  return s.toUpperCase().replace(/[^A-Z0-9]+/g, ' ').trim();
}

const NOISE = /\b(MOB|NIP|CR|DR|POS|PRCH|MC|LOC|TRF|TRANSFER|OUTWARD|INWARD|INSTANT|PAYMENT|VIA|GTWORLD|BETWEEN|CUSTOMERS|BANK|PLC|LIMITED|LTD|MICROFINANCE|LANG|LAGOS|NG|GTB|UBA)\b/g;

/**
 * Best guess at the payee's name in a bank description. The owner can edit it
 * before saving, so this only needs to be a sensible default.
 */
export function suggestPayeeKey(description: string): string {
  let s = description.toUpperCase();
  // Prefer the recipient: text after the last " TO " or after a "|" separator.
  const afterPipe = s.includes('|') ? s.slice(s.lastIndexOf('|') + 1) : null;
  const toMatch = [...s.matchAll(/\bTO\s+/g)].pop();
  const afterDashes = s.includes('--') ? s.slice(s.indexOf('--') + 2) : null;
  const afterMob = /\/MOB\//.test(s) ? s.split('/MOB/')[1] ?? null : null;
  if (afterPipe) s = afterPipe;
  else if (afterMob) s = afterMob.split('/')[0] ?? afterMob;
  else if (afterDashes) s = afterDashes;
  else if (toMatch?.index !== undefined) s = s.slice(toMatch.index + toMatch[0].length);
  // Cut at references, slashes, commas (POS branch addresses) and take the name after " - ".
  s = s.split('/')[0] ?? s;
  s = s.split(',')[0] ?? s;
  if (s.includes(' - ')) s = s.slice(s.lastIndexOf(' - ') + 3);
  s = s.replace(/\b[A-Z]*\d[A-Z0-9]*\b/g, ' ');
  s = normaliseText(s).replace(NOISE, ' ').replace(/\s+/g, ' ').trim();
  // Keep the first four words; enough to be specific, short enough to recur.
  const words = s.split(' ').filter((w) => w.length > 1).slice(0, 4);
  // The key must appear word-for-word in the description, or it will never match anything.
  // Take the longest run of at least two consecutive words that does; else suggest nothing.
  const text = ` ${normaliseText(description)} `;
  if (words.length === 1) return text.includes(` ${words[0]} `) ? words[0]! : '';
  for (let len = words.length; len >= 2; len--) {
    for (let i = 0; i + len <= words.length; i++) {
      const key = words.slice(i, i + len).join(' ');
      if (text.includes(` ${key} `)) return key;
    }
  }
  return '';
}

export interface PayeeRule {
  match_text: string;
  category_id: string;
  /** Optional limits: only debits or only credits, and an amount band. */
  direction?: 'debit' | 'credit' | null;
  min_amount?: number | null;
  max_amount?: number | null;
}

export function findPayeeRule(description: string, rules: PayeeRule[], tx?: { direction: 'debit' | 'credit'; amount: number }): PayeeRule | null {
  const text = ` ${normaliseText(description)} `;
  const fits = (r: PayeeRule) => !tx || (
    (!r.direction || r.direction === tx.direction)
    && (r.min_amount == null || tx.amount >= Number(r.min_amount))
    && (r.max_amount == null || tx.amount <= Number(r.max_amount)));
  // Longest key wins, so a specific rule beats a generic one.
  return [...rules]
    .sort((a, b) => b.match_text.length - a.match_text.length)
    .find((r) => text.includes(` ${normaliseText(r.match_text)} `) && fits(r)) ?? null;
}
